import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface NotificationState {
    open: boolean;
    message: string;
    severity: AlertColor;
    autoHideDuration?: number;
}

interface NotificationContextType {
    showNotification: (message: string, severity?: AlertColor, autoHideDuration?: number) => void;
    showSuccess: (message: string, autoHideDuration?: number) => void;
    showError: (message: string, autoHideDuration?: number) => void;
    showWarning: (message: string, autoHideDuration?: number) => void;
    showInfo: (message: string, autoHideDuration?: number) => void;
    hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
        autoHideDuration: 6000,
    });

    const showNotification = (
        message: string,
        severity: AlertColor = 'info',
        autoHideDuration: number = 6000
    ) => {
        setNotification({
            open: true,
            message,
            severity,
            autoHideDuration,
        });
    };

    const showSuccess = (message: string, autoHideDuration: number = 6000) => {
        showNotification(message, 'success', autoHideDuration);
    };

    const showError = (message: string, autoHideDuration: number = 6000) => {
        showNotification(message, 'error', autoHideDuration);
    };

    const showWarning = (message: string, autoHideDuration: number = 6000) => {
        showNotification(message, 'warning', autoHideDuration);
    };

    const showInfo = (message: string, autoHideDuration: number = 6000) => {
        showNotification(message, 'info', autoHideDuration);
    };

    const hideNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    const contextValue: NotificationContextType = {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideNotification,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}

            {/* Unified Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={notification.autoHideDuration}
                onClose={hideNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={hideNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        minWidth: '300px',
                        fontSize: '0.95rem',
                        boxShadow: 3,
                    }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

// Custom hook to use the notification context
export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};